<template>
  <div class="output-section" v-if="showComparisonOutput">
    <div class="output-header">
      <div class="header-content">
        <h3>📊 System Output</h3>
        <p v-if="!comparisonData || comparisonData.length === 0">Comparison results will be displayed here after processing</p>
        <p v-else>Showing comparison results</p>
      </div>
    </div>
    
    <div class="output-content">
      <div class="output-summary" v-if="comparisonStats">
        <p><strong>Total Rows:</strong> {{ comparisonStats.total }}</p>
        <p><strong>New:</strong> {{ comparisonStats.new }}</p>
        <p><strong>Changed:</strong> {{ comparisonStats.changed }}</p>
        <p><strong>Previous (with differences):</strong> {{ comparisonStats.previous }}</p>
        <p><strong>Deleted:</strong> {{ comparisonStats.deleted }}</p>
        <p><strong>Unchanged:</strong> {{ comparisonStats.unchanged }}</p>
        <p v-if="!isExpanded && showExpandButton" class="rows-info">
          <strong>Showing:</strong> {{ displayedComparisonData.length }} of {{ comparisonStats.total }} rows
        </p>
      </div>
      
      <!-- Expand/Collapse Button -->
      <div class="expand-controls" v-if="showExpandButton">
        <button 
          @click="toggleExpand" 
          class="expand-btn"
          :title="isExpanded ? 'Show fewer rows' : 'Show all rows'"
        >
          <span class="expand-icon">{{ isExpanded ? '📝' : '📋' }}</span>
          <span class="expand-text">{{ isExpanded ? 'Show Less' : `Show All (${comparisonStats.total})` }}</span>
        </button>
      </div>
      
      <div class="output-legend" v-if="comparisonData && comparisonData.length > 0">
        <div class="legend-content">
          <p><strong>Legend:</strong> Row background colors follow Row Type Legend | Left border colors indicate: 
            <span class="legend-status status-new-indicator">🟢 New</span>
            <span class="legend-status status-changed-indicator">🟡 Changed</span>
            <span class="legend-status status-previous-indicator">🔴 Previous</span>
            <span class="legend-status status-deleted-indicator">🔴 Deleted</span>
            <span class="legend-status status-unchanged-indicator">⚫ Unchanged</span>
          </p>
        </div>
      </div>
      
      <div class="output-table-container">
        <div v-if="!comparisonData || comparisonData.length === 0" class="placeholder-message">
          <p>🔄 Comparison table will be displayed here after processing</p>
          <p>Upload both Excel files and click "Process Files" to see the results</p>
        </div>
        
        <div v-else class="table-wrapper">
          <table class="output-table">
            <thead>
              <tr>
                <th v-for="(header, index) in comparisonHeaders" :key="index">{{ header }}</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="(row, index) in displayedComparisonData" 
                :key="index"
                :class="[
                  'output-row', 
                  `status-${row.status && row.status.toLowerCase()}`,
                  `row-type-${row.data && row.data[1] ? row.data[1].toLowerCase() : 'default'}`
                ]"
              >
                <td 
                  v-for="(cell, cellIndex) in row.data" 
                  :key="cellIndex"
                  :class="{ 'cell-different': row.differences && row.differences.includes(cellIndex) }"
                >
                  {{ cell || '' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from '@vue/composition-api'

export default {
  name: 'OutputSection',
  props: {
    comparisonData: {
      type: Array,
      default: () => []
    },
    comparisonHeaders: {
      type: Array,
      default: () => []
    },
    showComparisonOutput: {
      type: Boolean,
      default: false
    },
    isColumnVisible: {
      type: Function,
      required: true
    },
    isColumnVisibleByName: {
      type: Function,
      required: true
    }
  },
  setup(props) {
    // Expand/collapse state
    const isExpanded = ref(false)

    // Display limited rows (max 10) when not expanded
    const displayedComparisonData = computed(() => {
      if (!props.comparisonData) return []
      return isExpanded.value ? props.comparisonData : props.comparisonData.slice(0, 10)
    })

    // Check if expand button should be shown
    const showExpandButton = computed(() => {
      return props.comparisonData && props.comparisonData.length > 10
    })

    // Toggle expand/collapse
    const toggleExpand = () => {
      isExpanded.value = !isExpanded.value
    }

    // Computed properties
    const comparisonStats = computed(() => {
      if (!props.comparisonData || props.comparisonData.length === 0) {
        return null
      }
      
      const total = props.comparisonData.length
      const new_ = props.comparisonData.filter(row => row.status === 'New').length
      const changed = props.comparisonData.filter(row => row.status === 'Changed').length
      const deleted = props.comparisonData.filter(row => row.status === 'Deleted').length
      const unchanged = props.comparisonData.filter(row => row.status === 'Unchanged').length
      const previous = props.comparisonData.filter(row => row.status === 'Previous').length
      
      return {
        total,
        new: new_,
        changed,
        deleted,
        unchanged,
        previous
      }
    })

    // Reset when data changes
    watch(() => props.comparisonData, () => {
      // Reset any state if needed when data changes
    }, { deep: true })

    return {
      isExpanded,
      displayedComparisonData,
      showExpandButton,
      toggleExpand,
      comparisonStats
    }
  }
}
</script>

<style scoped>
/* Output Section Styles */
.output-section {
  margin-top: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.output-header {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 15px;
}

.header-content {
  text-align: center;
  flex: 1;
}

.output-header h3 {
  color: #2c3e50;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.output-header p {
  color: #6c757d;
  font-size: 14px;
  margin: 0;
}

/* Export Button Styles */
.output-content {
  background: white;
  border-radius: 6px;
  padding: 20px;
  border: 1px solid #dee2e6;
}

.output-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e9ecef;
  font-size: 14px;
  color: #6c757d;
}

.output-summary p {
  margin: 0;
}

/* Expand Controls */
.expand-controls {
  display: flex;
  justify-content: center;
  margin-bottom: 15px;
}

.expand-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(40,167,69,0.2);
}

.expand-btn:hover {
  background: #218838;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(40,167,69,0.3);
}

.expand-icon {
  font-size: 16px;
}

.expand-text {
  white-space: nowrap;
}

.rows-info {
  color: #666;
  font-style: italic;
}

.output-legend {
  margin-top: 15px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 4px;
  font-size: 12px;
  border: 1px solid #dee2e6;
}

.output-legend p {
  margin: 0;
  line-height: 1.4;
}

.legend-status {
  margin: 0 8px;
  font-size: 11px;
  white-space: nowrap;
}

.output-table-container {
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.placeholder-message {
  text-align: center;
  color: #6c757d;
  font-style: italic;
}

.placeholder-message p {
  margin: 8px 0;
}

.table-wrapper {
  width: 100%;
  overflow-x: auto;
}

/* Output table styles */
.output-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-top: 15px;
}

.output-table th,
.output-table td {
  padding: 8px 12px;
  text-align: left;
  border-bottom: 1px solid #dee2e6;
  border-right: 1px solid #dee2e6;
  max-height: 30px;
  height: 30px;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.output-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #495057;
  position: sticky;
  top: 0;
}

/* Row type background colors (same as DataTable) */
.output-row.row-type-item {
  background-color: #E9C1C2 !important;
}

.output-row.row-type-itemremark {
  background-color: #CFC6E1 !important;
}

.output-row.row-type-section {
  background-color: #FFE5B4 !important;
}

.output-row.row-type-subsection {
  background-color: #B2DAC3 !important;
}

.output-row.row-type-unit {
  background-color: #F6E1C6 !important;
}

.output-row.row-type-itemcategory {
  background-color: #C9D9ED !important;
}

.output-row.row-type-default {
  background-color: #fafafa !important;
}

/* Status-based left border for row status indication */
.status-new {
  border-left: 4px solid #28a745 !important;
}

.status-changed {
  border-left: 4px solid #ffc107 !important;
}

.status-previous {
  border-left: 4px solid #dc3545 !important;
  font-style: italic;
  opacity: 0.9;
}

.status-deleted {
  border-left: 4px solid #dc3545 !important;
  text-decoration: line-through;
  opacity: 0.8;
}

.status-unchanged {
  border-left: 4px solid #6c757d !important;
}

.output-row:hover {
  opacity: 0.9;
}

/* Highlight different cells in Previous rows */
.cell-different {
  background-color: #ffe6cc !important;
  border: 2px solid #ff9500 !important;
  font-weight: bold;
  position: relative;
}

.cell-different::after {
  content: '';
  position: absolute;
  top: 2px;
  right: 2px;
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid #ff9500;
}

/* Legend layout improvements */
.legend-content {
  margin-bottom: 10px;
}

.legend-content p {
  margin: 0;
}

/* Responsive Design for OutputSection */
@media (max-width: 768px) {
  .output-header {
    flex-direction: column;
    text-align: center;
    gap: 10px;
  }
  
  .header-content {
    order: 1;
  }
  
  .export-controls {
    order: 2;
    justify-content: center;
  }
  
  .output-summary {
    flex-direction: column;
    gap: 10px;
  }
  
  .summary-content {
    flex-direction: column;
    gap: 8px;
  }
  
  .summary-label {
    text-align: center;
  }
  
  .summary-counts {
    justify-content: center;
  }
}
</style>
